'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TotalConnectAccessGate } from '@/components/TotalConnectAccessGate';
import { X, ExternalLink, Maximize2, Minimize2, Calendar, Clock, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import CapacitorModalFilterHeader, { ModalFilterBar, capacitorHeaderActionClass } from '@/components/CapacitorModalFilterHeader';
import EnhancedCalendarModule from '@/app/client-zone/modules/total-connect/components/EnhancedCalendarModule';
import { useCalendarStore } from '@/app/client-zone/modules/total-connect/stores';
import type { CalendarEvent } from '@/app/client-zone/modules/total-connect/types';
import { useAuth } from '@/hooks/useAuth';
import { useModalState } from '@/contexts/ModalStateContext';
import { useTheme } from '@/contexts/ThemeContext';
import { isCapacitor } from '@/lib/platformUtils';
import { useNavigationRouter } from '@/hooks/useNavigationRouter';
import { useBadge } from '@/contexts/BadgeContext';
import { authService } from '@/lib/authService';
import calendarApiService from '@/app/client-zone/modules/total-connect/services/calendarApi.service';
import * as notify from '@/lib/notifications';
import { addDays, addMonths, addWeeks, differenceInMinutes, endOfMonth, endOfWeek, format, isAfter, isBefore, isSameDay, isToday, parseISO, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from 'date-fns';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShowDashboard?: () => void;
}

interface AppointmentWorkItem {
  id: string | number;
  title?: string;
  item_number?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  start_date?: string;
  due_date?: string;
  estimated_hours?: number | string;
  cl_location_name?: string;
  location_name?: string;
  cl_address?: string;
  location_address?: string;
  parent_job_number?: string;
  contact_name?: string;
  status?: string;
  description?: string;
}

export default function CalendarModal({ isOpen, onClose, onShowDashboard }: CalendarModalProps) {
  const { user, client } = useAuth();
  const selectedDate = useCalendarStore((state) => state.selectedDate);
  const calendarView = useCalendarStore((state) => state.view);
  const calendarEventsState = useCalendarStore((state) => state.events);
  const calendarLoading = useCalendarStore((state) => state.loading);
  const fetchCalendarEvents = useCalendarStore((state) => state.fetchEvents);
  const setSelectedDate = useCalendarStore((state) => state.setSelectedDate);
  const setCalendarView = useCalendarStore((state) => state.setView);
  const { registerBackHandler, unregisterBackHandler } = useModalState();
  const { currentTheme, clientBranding } = useTheme();
  const { badgeCounts } = useBadge();
  const router = useNavigationRouter();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [newEventRefresh, setNewEventRefresh] = useState(0);
  const [appointmentEvents, setAppointmentEvents] = useState<CalendarEvent[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const isMobile = isCapacitor();
  
  // Theme colors
  const primaryColor = clientBranding?.primary_color || currentTheme?.colors?.primary || '#3b82f6';
  const isDark = currentTheme?.mode === 'dark';
  const headerActionClass = capacitorHeaderActionClass(isDark);

  const parseCalendarEventDate = useCallback((value: Date | string) => {
    return typeof value === 'string' ? parseISO(value) : new Date(value);
  }, []);

  const calendarEvents = useMemo(() => {
    const mergedEvents = new Map<string, CalendarEvent>();

    for (const event of calendarEventsState) {
      mergedEvents.set(event.id, event);
    }

    for (const event of appointmentEvents) {
      if (!mergedEvents.has(event.id)) {
        mergedEvents.set(event.id, event);
      }
    }

    return Array.from(mergedEvents.values());
  }, [appointmentEvents, calendarEventsState]);

  const selectedDayEvents = useMemo(() => {
    return calendarEvents
      .filter((event) => isSameDay(parseCalendarEventDate(event.start), selectedDate))
      .sort((a, b) => parseCalendarEventDate(a.start).getTime() - parseCalendarEventDate(b.start).getTime());
  }, [calendarEvents, parseCalendarEventDate, selectedDate]);

  const scheduledHours = useMemo(() => {
    return selectedDayEvents.reduce((total, event) => {
      if (event.allDay) {
        return total + 8;
      }

      const start = parseCalendarEventDate(event.start);
      const end = parseCalendarEventDate(event.end);
      const durationMinutes = Math.max(30, differenceInMinutes(end, start));
      return total + durationMinutes / 60;
    }, 0);
  }, [parseCalendarEventDate, selectedDayEvents]);

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });

    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(weekStart, index);
      const dateHasEvents = calendarEvents.some((event) => isSameDay(parseCalendarEventDate(event.start), date));

      return {
        date,
        key: format(date, 'yyyy-MM-dd'),
        dayLabel: format(date, 'EE'),
        dayNumber: format(date, 'd'),
        isCurrentDay: isToday(date),
        hasEvents: dateHasEvents,
      };
    });
  }, [calendarEvents, parseCalendarEventDate, selectedDate]);

  const selectedDateSummary = useMemo(() => {
    if (isToday(selectedDate)) {
      return 'Today';
    }

    return format(selectedDate, 'EEEE, d MMMM');
  }, [selectedDate]);

  const expandedCalendarTitle = useMemo(() => {
    switch (calendarView) {
      case 'month':
        return format(selectedDate, 'MMMM yyyy');
      case 'week': {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
        return `${format(weekStart, 'd MMM')} - ${format(weekEnd, 'd MMM yyyy')}`;
      }
      case 'day':
      default:
        return format(selectedDate, 'EEEE, d MMMM yyyy');
    }
  }, [calendarView, selectedDate]);

  const getEventAccent = useCallback((event: CalendarEvent) => {
    return event.teamColor || event.color || (event.syncSource === 'smarttask-appointment' ? '#8b5cf6' : primaryColor);
  }, [primaryColor]);

  const getEventStateLabel = useCallback((event: CalendarEvent) => {
    if (event.allDay) {
      return 'All day';
    }

    const now = new Date();
    const start = parseCalendarEventDate(event.start);
    const end = parseCalendarEventDate(event.end);

    if (isBefore(end, now)) {
      return 'Past';
    }

    if (isAfter(start, now) && differenceInMinutes(start, now) <= 60) {
      return 'Up next';
    }

    if (!isBefore(now, start) && !isAfter(now, end)) {
      return 'Now';
    }

    return 'Scheduled';
  }, [parseCalendarEventDate]);

  const shiftSelectedDate = useCallback((direction: -1 | 1) => {
    setSelectedDate(addWeeks(selectedDate, direction));
  }, [selectedDate, setSelectedDate]);

  const handleExpandedCalendarNav = useCallback((direction: -1 | 1) => {
    switch (calendarView) {
      case 'month':
        setSelectedDate(direction > 0 ? addMonths(selectedDate, 1) : subMonths(selectedDate, 1));
        break;
      case 'week':
        setSelectedDate(direction > 0 ? addWeeks(selectedDate, 1) : subWeeks(selectedDate, 1));
        break;
      case 'day':
      default:
        setSelectedDate(direction > 0 ? addDays(selectedDate, 1) : subDays(selectedDate, 1));
        break;
    }
  }, [calendarView, selectedDate, setSelectedDate]);

  const appointmentRange = useMemo(() => {
    const start = startOfMonth(subMonths(selectedDate, 1));
    const end = endOfMonth(addMonths(selectedDate, 1));
    return {
      startIso: start.toISOString(),
      endIso: end.toISOString(),
    };
  }, [selectedDate]);

  const mapAppointmentToCalendarEvent = useCallback((appointment: AppointmentWorkItem): CalendarEvent | null => {
    const scheduledStart = appointment.scheduled_start || appointment.start_date || appointment.due_date;
    if (!scheduledStart) {
      return null;
    }

    const start = new Date(scheduledStart);
    if (Number.isNaN(start.getTime())) {
      return null;
    }

    const preferredEnd = appointment.scheduled_end
      ? new Date(appointment.scheduled_end)
      : new Date(start.getTime() + Math.max(Number(appointment.estimated_hours || 1), 0.5) * 60 * 60 * 1000);
    const end = Number.isNaN(preferredEnd.getTime())
      ? new Date(start.getTime() + 60 * 60 * 1000)
      : preferredEnd;

    const location = appointment.cl_location_name
      || appointment.location_name
      || appointment.cl_address
      || appointment.location_address
      || undefined;

    const descriptionParts = [
      appointment.parent_job_number ? `Parent job: ${appointment.parent_job_number}` : null,
      appointment.contact_name ? `Contact: ${appointment.contact_name}` : null,
      appointment.status ? `Status: ${String(appointment.status).replace(/_/g, ' ')}` : null,
      appointment.description || null,
    ].filter(Boolean);

    return {
      id: `smarttask-appointment:${appointment.id}`,
      title: appointment.item_number
        ? `${appointment.item_number} · ${appointment.title || 'Untitled appointment'}`
        : appointment.title || 'Untitled appointment',
      start,
      end,
      allDay: false,
      location,
      description: descriptionParts.join('\n'),
      color: '#8b5cf6',
      teamName: 'Smart Task',
      teamColor: '#8b5cf6',
      calendarId: 'smarttask-appointments',
      status: appointment.status === 'cancelled' ? 'cancelled' : 'confirmed',
      syncSource: 'smarttask-appointment',
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !user || !client) {
      return;
    }

    const token = authService.getTokens()?.access_token;
    if (!token) {
      setAppointmentEvents([]);
      return;
    }

    const controller = new AbortController();

    const loadAppointments = async () => {
      try {
        const searchParams = new URLSearchParams({
          item_type: 'appointment',
          filter_by_assigned_user: 'true',
          field_worker_mode: 'true',
          scheduled_start_from: appointmentRange.startIso,
          scheduled_start_to: appointmentRange.endIso,
          sort_by: 'scheduled_start',
          sort_order: 'asc',
          limit: '200',
        });

        const response = await fetch(`/api/v1/work-items?${searchParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || data?.success === false) {
          throw new Error(data?.error || 'Failed to load appointments');
        }

        const mappedEvents = Array.isArray(data?.data)
          ? data.data
              .map((appointment: AppointmentWorkItem) => mapAppointmentToCalendarEvent(appointment))
              .filter((event: CalendarEvent | null): event is CalendarEvent => Boolean(event))
          : [];

        setAppointmentEvents(mappedEvents);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        console.error('[CalendarModal] Failed to load Smart Task appointments:', error);
        setAppointmentEvents([]);
      }
    };

    void loadAppointments();

    return () => controller.abort();
  }, [appointmentRange.endIso, appointmentRange.startIso, client, isOpen, mapAppointmentToCalendarEvent, user]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setCalendarExpanded(false);
    setSelectedDate(new Date());
    setCalendarView('day');
  }, [isOpen, setCalendarView, setSelectedDate]);

  useEffect(() => {
    if (!isOpen || !user || !client) {
      return;
    }

    const start = startOfMonth(subMonths(selectedDate, 1));
    const end = endOfMonth(addMonths(selectedDate, 1));
    void fetchCalendarEvents(start, end);
  }, [client, fetchCalendarEvents, isOpen, newEventRefresh, selectedDate, user]);

  useEffect(() => {
    if (calendarExpanded && calendarView === 'agenda') {
      setCalendarView('day');
    }
  }, [calendarExpanded, calendarView, setCalendarView]);

  // On Capacitor, show main Header when modal opens
  useEffect(() => {
    if (isOpen && isMobile) {
      window.dispatchEvent(new Event('showHeader'));
    }
  }, [isOpen, isMobile]);

  // Register back button handler for mobile
  useEffect(() => {
    if (!isOpen) return;

    const handleBack = (): boolean => {
      if (showNewEvent) {
        console.log('[CalendarModal] 🔙 Closing new event panel');
        setShowNewEvent(false);
        return true;
      }

      if (calendarExpanded) {
        setCalendarExpanded(false);
        return true;
      }

      if (onShowDashboard) {
        console.log('[CalendarModal] 🔙 Returning to dashboard shell');
        onShowDashboard();
        return true;
      }

      onClose();
      return true; // Handled
    };

    registerBackHandler(handleBack);
    return () => unregisterBackHandler(handleBack);
  }, [calendarExpanded, isOpen, onClose, onShowDashboard, registerBackHandler, showNewEvent, unregisterBackHandler]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOpenFullPage = () => {
    onClose();
    router.push('/client-zone/modules/total-connect?view=calendar');
  };

  // Capacitor: raised above CapacitorFooter (z-[90]) — the modal owns the full screen.
  const modalBackdropZIndexClass = isMobile ? 'z-[99]' : 'z-[89]';
  const modalPanelZIndexClass = isMobile ? 'z-[100]' : 'z-[90]';

  return (
    <>
      {/* Backdrop overlay - not clickable on Capacitor where footer handles navigation */}
      {!isMobile && (
        <div 
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300 ${modalBackdropZIndexClass}`}
          onClick={onClose}
        />
      )}
      
      {/* Modal Container - slide in from right like Messages modal */}
      <div
        ref={modalRef}
        className={`${
          isFullScreen || isMobile
            ? `fixed inset-x-0 ${modalPanelZIndexClass}`
            : `fixed ${modalPanelZIndexClass} right-0 flex h-full w-full max-w-5xl animate-slide-in-right flex-col shadow-2xl`
        } flex flex-col ${isDark ? 'bg-gray-900' : 'bg-white'} ${isFullScreen || isMobile ? 'animate-slide-in-right shadow-2xl' : ''}`}
        style={isMobile ? {
          top: '0px',
          bottom: 'var(--safe-area-inset-bottom)',
          transition: 'top 300ms ease'
        } : {
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          top: 'var(--safe-area-inset-top)',
          bottom: 'var(--safe-area-inset-bottom)',
          height: 'calc(100% - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))'
        }}
      >
        {/* Header - shared centered-icon + filter-bar modal chrome */}
        <CapacitorModalFilterHeader
          icon={Calendar}
          title="Calendar"
          isDark={isDark}
          onClose={onClose}
          applyTopSafeArea={isMobile}
          subtitle={
            <>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                {selectedDateSummary}
              </span>
              {selectedDayEvents.length > 0 && (
                <>
                  <span className="text-[10px] text-gray-300 dark:text-gray-600">&bull;</span>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {selectedDayEvents.length} item{selectedDayEvents.length !== 1 ? 's' : ''} &bull; {scheduledHours.toFixed(1)}h
                  </span>
                </>
              )}
              {!selectedDayEvents.length && badgeCounts.calendarUnreadCount > 0 && (
                <span className="text-[10px] font-medium" style={{ color: primaryColor }}>
                  {badgeCounts.calendarUnreadCount} upcoming
                </span>
              )}
            </>
          }
          actions={
            !isMobile ? (
              <>
                <button
                  type="button"
                  onClick={handleOpenFullPage}
                  className={headerActionClass}
                  title="Open in Full Page"
                  aria-label="Open in Full Page"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  className={headerActionClass}
                  title={isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  aria-label={isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {isFullScreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
              </>
            ) : undefined
          }
          filterBar={
            <ModalFilterBar
              isDark={isDark}
              filters={{
                options: [
                  { value: 'day', label: 'Day' },
                  { value: 'week', label: 'Week' },
                  { value: 'month', label: 'Month' },
                ],
                value: calendarView === 'agenda' ? 'day' : calendarView,
                onChange: (v) => setCalendarView(v as 'day' | 'week' | 'month'),
              }}
              onCreateNew={() => setShowNewEvent(true)}
              createLabel="New event"
            />
          }
        />

        <div className={`px-3 pt-3 pb-2 flex-shrink-0 ${isDark ? 'bg-gray-800/60 border-b border-gray-700/80' : 'bg-gray-50/80 border-b border-gray-200/80'}`}>
          <div className={`rounded-2xl border p-2 shadow-sm ${isDark ? 'border-gray-700/80 bg-gray-900/60' : 'border-gray-200/90 bg-white/90'}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className={`inline-flex items-center gap-1 rounded-xl border p-1 ${isDark ? 'border-gray-700 bg-gray-800/90' : 'border-gray-200 bg-gray-50/95'}`}>
              <button
                onClick={() => shiftSelectedDate(-1)}
                className="p-1.5 rounded-lg text-slate-600 transition-colors hover:bg-slate-100/80 dark:text-slate-300 dark:hover:bg-slate-700/80"
                title="Previous week"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setSelectedDate(new Date())}
                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold shadow-sm"
                style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
              >
                Today
              </button>
              <button
                onClick={() => shiftSelectedDate(1)}
                className="p-1.5 rounded-lg text-slate-600 transition-colors hover:bg-slate-100/80 dark:text-slate-300 dark:hover:bg-slate-700/80"
                title="Next week"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              </div>

              <button
                onClick={() => setCalendarExpanded((expanded) => !expanded)}
                className={`inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs font-medium shadow-sm transition-colors ${isDark ? 'border-gray-700 bg-gray-800/90 text-gray-300 hover:bg-gray-700/80' : 'border-gray-200 bg-gray-50/95 text-slate-600 hover:bg-slate-100/90'}`}
              >
                {calendarExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {calendarExpanded ? 'Hide' : 'Calendar'}
              </button>
            </div>

            <div className={`grid grid-cols-7 gap-1 rounded-2xl border p-1.5 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-gray-50/90'}`}>
              {weekDays.map((day) => (
                <button
                  key={day.key}
                  onClick={() => setSelectedDate(day.date)}
                  className={`flex min-w-0 flex-col items-center rounded-xl py-2 transition-all ${
                    isSameDay(selectedDate, day.date)
                      ? 'text-white shadow-sm'
                      : day.isCurrentDay
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-sky-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                  style={isSameDay(selectedDate, day.date) ? { backgroundColor: primaryColor } : undefined}
                >
                  <span className="text-[10px] font-medium">{day.dayLabel}</span>
                  <span className="text-sm font-bold leading-tight">{day.dayNumber}</span>
                  {day.hasEvents && !isSameDay(selectedDate, day.date) && (
                    <div className="mt-1 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <TotalConnectAccessGate featureName="Calendar">
            {user && client ? (
              calendarExpanded ? (
                <div className="h-full min-h-0 px-3 pb-3">
                  <div className={`h-full min-h-0 rounded-[1.5rem] border overflow-hidden flex flex-col ${isDark ? 'border-gray-700 bg-gray-900/80' : 'border-gray-200 bg-white/95'} shadow-sm`}>
                    <div className={`px-3 py-3 border-b ${isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50/90'}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className={`inline-flex items-center gap-1 rounded-xl border p-1 ${isDark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-white/90'}`}>
                          <button
                            onClick={() => handleExpandedCalendarNav(-1)}
                            className="p-1.5 rounded-lg text-slate-600 transition-colors hover:bg-slate-100/80 dark:text-slate-300 dark:hover:bg-slate-700/80"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setSelectedDate(new Date())}
                            className="rounded-lg px-2.5 py-1.5 text-xs font-semibold shadow-sm"
                            style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
                          >
                            Today
                          </button>
                          <button
                            onClick={() => handleExpandedCalendarNav(1)}
                            className="p-1.5 rounded-lg text-slate-600 transition-colors hover:bg-slate-100/80 dark:text-slate-300 dark:hover:bg-slate-700/80"
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        <div className={`inline-flex rounded-xl border p-1 ${isDark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-white/90'}`}>
                          {[
                            { id: 'day', label: 'Day' },
                            { id: 'week', label: 'Week' },
                            { id: 'month', label: 'Month' },
                          ].map((option) => (
                            <button
                              key={option.id}
                              onClick={() => setCalendarView(option.id as 'day' | 'week' | 'month')}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                calendarView === option.id
                                  ? 'text-white shadow-sm'
                                  : isDark
                                  ? 'text-gray-300 hover:text-white'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                              style={calendarView === option.id ? { backgroundColor: primaryColor } : undefined}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                        {expandedCalendarTitle}
                      </div>
                    </div>

                    <div className="flex-1 min-h-0">
                      <EnhancedCalendarModule
                        clientId={client.id}
                        userId={user.id}
                        externalEvents={appointmentEvents}
                        hideHeader
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full overflow-y-auto px-3 pb-6">
                  {calendarLoading && calendarEvents.length === 0 ? (
                    <div className="flex items-center justify-center h-full min-h-[280px]">
                      <div className="text-center">
                        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
                        <p className="text-gray-500 dark:text-gray-400">Loading schedule...</p>
                      </div>
                    </div>
                  ) : selectedDayEvents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Calendar className="w-14 h-14 text-gray-300 dark:text-gray-600 mb-3" />
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No items scheduled</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {isToday(selectedDate) ? 'Nothing scheduled for today' : `No calendar items on ${format(selectedDate, 'EEEE')}`}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 pt-1">
                      {selectedDayEvents.map((event) => {
                        const accent = getEventAccent(event);
                        const start = parseCalendarEventDate(event.start);
                        const end = parseCalendarEventDate(event.end);
                        const sourceLabel = event.syncSource === 'smarttask-appointment' ? 'Smart Task' : 'Calendar';

                        return (
                          <div
                            key={event.id}
                            className={`rounded-2xl border px-3 py-3 shadow-sm ${isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-white/95'}`}
                            style={{ borderLeftWidth: '4px', borderLeftColor: accent }}
                          >
                            <div className="flex items-center gap-2 mb-2 text-[10px] font-semibold uppercase tracking-wide">
                              <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: `${accent}18`, color: accent }}>
                                {sourceLabel}
                              </span>
                              <span className="text-gray-400 dark:text-gray-500">{getEventStateLabel(event)}</span>
                            </div>

                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight line-clamp-2">
                                  {event.title}
                                </p>

                                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  <Clock className="w-3 h-3 flex-shrink-0" />
                                  <span>
                                    {event.allDay ? 'All day' : `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`}
                                  </span>
                                </div>

                                {event.location && (
                                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{event.location}</span>
                                  </div>
                                )}

                                {event.description && (
                                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap line-clamp-2">
                                    {event.description}
                                  </p>
                                )}
                              </div>

                              <div className="text-right flex-shrink-0">
                                <div className="text-lg font-semibold text-gray-900 dark:text-white leading-none">
                                  {event.allDay ? 'All' : format(start, 'HH:mm')}
                                </div>
                                {!event.allDay && (
                                  <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                    {Math.max(30, differenceInMinutes(end, start))} min
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
                  <p className="text-gray-500 dark:text-gray-400">Loading...</p>
                </div>
              </div>
            )}
          </TotalConnectAccessGate>
        </div>

      </div>
      
      {/* New Event Modal */}
      {showNewEvent && (
        <NewEventModal
          isOpen={showNewEvent}
          onClose={() => setShowNewEvent(false)}
          onCreated={() => {
            setShowNewEvent(false);
            setNewEventRefresh(prev => prev + 1);
          }}
          isDark={isDark}
          primaryColor={primaryColor}
        />
      )}
    </>
  );
}

// New Event Modal Component
interface NewEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  isDark: boolean;
  primaryColor: string;
}

function NewEventModal({ isOpen, onClose, onCreated, isDark, primaryColor }: NewEventModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [attendees, setAttendees] = useState('');
  const [saving, setSaving] = useState(false);
  // Contact picker (CRM + personal — same source as email compose)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerContacts, setPickerContacts] = useState<Array<{ name?: string; email: string; source: 'crm' | 'personal' }>>([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const selectedEmails = new Set(
    attendees.split(/[,\s]+/).map((e) => e.trim().toLowerCase()).filter(Boolean)
  );
  const toggleAttendee = (email: string) => {
    const list = attendees.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean);
    const idx = list.findIndex((e) => e.toLowerCase() === email.toLowerCase());
    if (idx >= 0) list.splice(idx, 1); else list.push(email);
    setAttendees(list.join(', '));
  };
  const loadPickerContacts = async () => {
    if (pickerContacts.length > 0 || pickerLoading) return;
    setPickerLoading(true);
    try {
      const tokens = authService.getTokens();
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${tokens?.access_token}` };
      const post = (action: string) => fetch('/api/v1/email-contacts', {
        method: 'POST', headers, body: JSON.stringify({ action, limit: 100 }),
      }).then((r) => r.json()).catch(() => ({}));
      const [crm, personal] = await Promise.all([post('getCrmContacts'), post('getMyContacts')]);
      const seen = new Set<string>();
      const list: typeof pickerContacts = [];
      const push = (c: { name?: string; email?: string }, source: 'crm' | 'personal') => {
        if (!c.email) return;
        const k = c.email.toLowerCase();
        if (seen.has(k)) return;
        seen.add(k);
        list.push({ name: c.name, email: c.email, source });
      };
      [...(crm.data?.contactPeople || []), ...(crm.data?.contacts || [])].forEach((c) => push(c, 'crm'));
      (personal.data || []).forEach((c: { name?: string; email?: string }) => push(c, 'personal'));
      setPickerContacts(list);
    } finally {
      setPickerLoading(false);
    }
  };
  
  // Initialize with current date/time
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const hours = now.getHours();
      const roundedHour = hours + 1;
      const timeStr = `${roundedHour.toString().padStart(2, '0')}:00`;
      const endHour = roundedHour + 1;
      const endTimeStr = `${endHour.toString().padStart(2, '0')}:00`;
      
      setStartDate(dateStr);
      setEndDate(dateStr);
      setStartTime(timeStr);
      setEndTime(endTimeStr);
    }
  }, [isOpen]);
  
  const handleCreate = async () => {
    if (!title.trim()) {
      notify.error('Please enter an event title');
      return;
    }

    setSaving(true);

    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        notify.error('Not authenticated');
        return;
      }

      const startDateTime = allDay 
        ? new Date(`${startDate}T00:00:00`).toISOString()
        : new Date(`${startDate}T${startTime}`).toISOString();
      const endDateTime = allDay
        ? new Date(`${endDate}T23:59:59`).toISOString()
        : new Date(`${endDate}T${endTime}`).toISOString();
      
      const attendeeList = attendees.split(/[,\s]+/)
        .map((e) => e.trim()).filter(Boolean)
        .map((email) => ({ email }));
      // Via the shared calendar service — Go handler emails invites when
      // attendees are present and auto-generates meeting_url for online.
      const data = await calendarApiService.createEvent({
        title: title.trim(),
        description: description.trim() || undefined,
        start: new Date(startDateTime),
        end: new Date(endDateTime),
        allDay,
        attendees: attendeeList.length > 0 ? attendeeList : undefined,
        event_type: attendeeList.length > 0 ? 'online' : 'in-person',
      } as never);
      
      if (data.success) {
        notify.success('Event created');
        onCreated();
        // Reset form
        setTitle('');
        setDescription('');
        setAllDay(false);
      } else {
        notify.error(data.error || 'Failed to create event');
      }
    } catch (error) {
      console.error('Create event error:', error);
      notify.error('Failed to create event');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[120] flex justify-end">
      <div
        className={`absolute inset-0 backdrop-blur-sm ${isDark ? 'bg-gray-900/70' : 'bg-gray-800/60'}`}
        onClick={onClose}
      />

      {/* Modal content */}
      <div
        className={`relative w-full max-w-lg h-full flex flex-col ${isDark ? 'bg-gray-800' : 'bg-white'} border-l ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Header - styled like list view */}
        <div 
          className="px-3 py-3 text-white flex-shrink-0"
          style={{ backgroundColor: primaryColor }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="p-1.5 bg-white/20 backdrop-blur rounded-lg flex-shrink-0">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-white">
                  New Event
                </h2>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
        
        {/* Form content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Title */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Event Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Team Meeting"
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
          </div>
          
          {/* Participants — free-type or pick from CRM/personal contacts */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Participants
              </label>
              <button
                type="button"
                onClick={() => { setPickerOpen((o) => !o); loadPickerContacts(); }}
                className={`text-xs px-2 py-1 rounded-md ${isDark ? 'bg-gray-700 text-blue-300 hover:bg-gray-600' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
              >
                {pickerOpen ? 'Hide contacts' : 'Pick from contacts'}
              </button>
            </div>
            <input
              type="text"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="email@example.com, another@example.com"
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            {pickerOpen && (
              <div className={`mt-2 max-h-48 overflow-y-auto rounded-lg border ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                {pickerLoading && (
                  <p className={`text-xs p-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading contacts…</p>
                )}
                {!pickerLoading && pickerContacts.length === 0 && (
                  <p className={`text-xs p-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No contacts found — type an email above</p>
                )}
                {pickerContacts.map((c) => (
                  <label key={c.email} className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`}>
                    <input
                      type="checkbox"
                      checked={selectedEmails.has(c.email.toLowerCase())}
                      onChange={() => toggleAttendee(c.email)}
                    />
                    <span className={`truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {c.name ? `${c.name} ` : ''}<span className={isDark ? 'text-gray-400' : 'text-gray-500'}>&lt;{c.email}&gt;</span>
                    </span>
                    <span className={`ml-auto text-[10px] uppercase ${c.source === 'crm' ? 'text-purple-400' : 'text-emerald-400'}`}>{c.source}</span>
                  </label>
                ))}
              </div>
            )}
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Calendar invites are emailed to each address
            </p>
          </div>

          {/* Description */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about this event..."
              rows={3}
              className={`w-full px-4 py-2 rounded-lg border resize-none ${
                isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
          </div>
          
          {/* All Day Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allDay"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="allDay" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              All day event
            </label>
          </div>
          
          {/* Start Date/Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (!endDate || e.target.value > endDate) {
                    setEndDate(e.target.value);
                  }
                }}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>
            {!allDay && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Time
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
            )}
          </div>
          
          {/* End Date/Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>
            {!allDay && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Time
                </label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Footer with Create button */}
        <div className={`p-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex-shrink-0`}>
          <button
            onClick={handleCreate}
            disabled={saving || !title.trim()}
            className="w-full py-2.5 px-4 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: primaryColor }}
          >
            {saving ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
